import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import dayjs from "dayjs";
import moment from "moment";
import { z } from "zod";

import { CustomCalendar } from "components/CustomCalendar/CustomCalendar";
import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { Select } from "components/ui/Select/Select";
import { useAddStandardException } from "features/appointments/hooks/useAddStandardException";
import { useEditStandardException } from "features/appointments/hooks/useEditStandardException";
import { reportFormErrors } from "utils/reportFormErrors";

import style from "./AddExceptionForm.module.css";

const formSchema = z
  .object({
    reason: z.string(),
    type: z.string().min(1, { message: "Please fill in the type." }),
    doctor: z.string().min(1, { message: "Please select doctor." }),
    fromDate: z.string().min(1, {
      message: "Please select the from date field.",
    }),
    toDate: z.string().min(1, {
      message: "Please select the to date field.",
    }),
    fromTime: z.string(),
    toTime: z.string(),
    allDay: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.fromDate && data.toDate && data.toDate < data.fromDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date must be after start date.",
        path: ["toDate"],
      });
    }
    if (!data.allDay) {
      if (!data.fromTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please select the from time field.",
          path: ["fromTime"],
        });
      }
      if (!data.toTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please select the to time field.",
          path: ["toTime"],
        });
      }
    }
  });

export const AddExceptionForm = ({
  initialData,
  closeModal,
  allTimes,
  staffMembers,
  initialStaffMember,
  refetchCalendar,
  initialStartDate,
  initialStartTime,
  exceptionTypes,
}) => {
  const [fromDateValue, setFromDateValue] = useState(() => {
    if (initialData?.startDate) {
      return dayjs(initialData.startDate, "YYYY-MM-DD").toDate();
    }
    if (initialStartDate) {
      return dayjs(initialStartDate, "DD-MM-YYYY").toDate();
    }
    return new Date();
  });
  const [toDateValue, setToDateValue] = useState(() => {
    if (initialData?.endDate) {
      return dayjs(initialData.endDate, "YYYY-MM-DD").toDate();
    }
    if (initialStartDate) {
      return dayjs(initialStartDate, "DD-MM-YYYY").toDate();
    }
    return new Date();
  });

  const { mutate: addStandardException, isPending: isAddPending } =
    useAddStandardException({
      onSuccess: () => {
        closeModal();
        refetchCalendar?.();
      },
    });

  const { mutate: editStandardException, isPending: isEditPending } =
    useEditStandardException({
      onSuccess: () => {
        closeModal();
        refetchCalendar?.();
      },
    });

  const initialValuesStartTime =
    initialData?.startAt &&
    initialData?.startAt
      ?.map((time) => (time >= 10 ? `${time}` : `0${time}`))
      ?.join(":");

  const initialValuesEndTime =
    initialData?.endAt &&
    initialData?.endAt
      ?.map((time) => (time >= 10 ? `${time}` : `0${time}`))
      ?.join(":");

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      // Editing shows the exception's staff member. New from a column
      // uses the column's doctor (initialStaffMember). New from no
      // column context falls back to the first doctor in the dropdown,
      // not the logged-in user.
      doctor:
        initialData?.staffMemberId != null
          ? String(initialData.staffMemberId)
          : initialStaffMember != null
          ? String(initialStaffMember)
          : staffMembers?.[0]?.id != null
          ? String(staffMembers[0].id)
          : "",
      type: initialData?.exceptionType || exceptionTypes[0] || "",
      reason: initialData?.exceptionReason || "",
      fromDate: initialData?.startDate
        ? dayjs(initialData.startDate, "YYYY-MM-DD").format("YYYY-MM-DD")
        : initialStartDate
        ? dayjs(initialStartDate, "DD-MM-YYYY").format("YYYY-MM-DD")
        : format(new Date(), "yyyy-MM-dd"),
      toDate: initialData?.endDate
        ? dayjs(initialData.endDate, "YYYY-MM-DD").format("YYYY-MM-DD")
        : initialStartDate
        ? dayjs(initialStartDate, "DD-MM-YYYY").format("YYYY-MM-DD")
        : format(new Date(), "yyyy-MM-dd"),
      fromTime:
        initialValuesStartTime ||
        initialStartTime ||
        allTimes[0].formattedRange.split(" - ")[0],
      toTime:
        initialValuesEndTime ||
        allTimes
          .find(
            (item) =>
              item?.formattedRange?.split(" - ")?.[0] === initialStartTime
          )
          ?.formattedRange?.split(" - ")[1] ||
        allTimes[0].formattedRange.split(" - ")[1],
      allDay: initialData?.isAllDay || false,
    },
  });

  const handleInvalidSubmit = (errors) =>
    reportFormErrors({ formName: "AddExceptionForm", errors });

  const handleFormSubmit = async (values) => {
    if (initialData) {
      editStandardException({
        staffMemberId: Number(values.doctor),
        type: values.type,
        startAt: values.fromTime,
        endAt: values.toTime,
        startTime: values.fromDate,
        endTime: values.toDate,
        exceptionId: initialData.exceptionId,
      });
    } else {
      addStandardException({
        staffMemberId: Number(values.doctor),
        type: values.type,
        startAt: values.fromTime,
        endAt: values.toTime,
        startTime: values.fromDate,
        endTime: values.toDate,
        reason: values.reason,
        allDay: values.allDay,
      });
    }
  };

  return (
    <>
      <FormProvider {...form}>
        <form
          className={style.addLetterForm}
          onSubmit={form.handleSubmit(handleFormSubmit, handleInvalidSubmit)}
        >
          <div className={style.inputGroupGrid} style={{ maxWidth: "100%" }}>
            <FormField name="doctor">
              <label className={style.addLetterFormLabel}>Doctor</label>
              <Select
                name="doctor"
                options={staffMembers.map((staffMember) => ({
                  label: `${staffMember.active ? "● " : ""}${
                    staffMember.firstName
                  } ${staffMember.lastName}${
                    staffMember.staffRole
                      ? ` (${staffMember.staffRole.toLowerCase()})`
                      : ""
                  }`,
                  value: String(staffMember.id),
                }))}
                value={form.watch("doctor")}
                onChange={(value) => form.setValue("doctor", value)}
                placeholder={null}
                fullWidth
              />
            </FormField>
            <FormField name="type">
              <label className={style.addLetterFormLabel}>Type</label>
              <Select
                name="type"
                options={(exceptionTypes || []).map((exceptionType) => ({
                  label: exceptionType,
                  value: exceptionType,
                }))}
                value={form.watch("type")}
                onChange={(value) => form.setValue("type", value)}
                placeholder={null}
                fullWidth
              />
            </FormField>
          </div>
          <div className={style.inputGroupGrid4}>
            <FormField name="fromDate">
              <label className={style.addLetterFormLabel}>From date</label>
              <CustomCalendar
                value={fromDateValue}
                onChange={(date) => {
                  setFromDateValue(date);
                  if (date) {
                    form.setValue(
                      "fromDate",
                      moment(date).format("YYYY-MM-DD")
                    );
                  }
                }}
                placeholder="Select from date"
              />
            </FormField>

            <FormField name="fromTime">
              <label className={style.addLetterFormLabel}>From time</label>
              <Select
                name="fromTime"
                options={allTimes.map((time) => ({
                  label: time.formattedRange.split(" - ")[0],
                  value: time.formattedRange.split(" - ")[0],
                }))}
                value={form.watch("fromTime")}
                onChange={(value) => form.setValue("fromTime", value)}
                disabled={!!form.watch("allDay")}
                placeholder={null}
                fullWidth
              />
            </FormField>
            <FormField name="toDate">
              <label className={style.addLetterFormLabel}>To date</label>
              <CustomCalendar
                value={toDateValue}
                onChange={(date) => {
                  setToDateValue(date);
                  if (date) {
                    form.setValue("toDate", moment(date).format("YYYY-MM-DD"));
                  }
                }}
                placeholder="Select to date"
              />
            </FormField>

            <FormField name="toTime">
              <label className={style.addLetterFormLabel}>To time</label>
              <Select
                name="toTime"
                options={allTimes.map((time) => ({
                  label: time.formattedRange.split(" - ")[1],
                  value: time.formattedRange.split(" - ")[1],
                }))}
                value={form.watch("toTime")}
                onChange={(value) => form.setValue("toTime", value)}
                disabled={!!form.watch("allDay")}
                placeholder={null}
                fullWidth
              />
            </FormField>
          </div>
          <div className={style.checkboxWrapper}>
            <label className={`${style.pregnancyFormCheckboxLabel}`}>
              <CustomCheckbox
                checked={form.watch("allDay")}
                onChange={(e) => {
                  form.clearErrors();
                  form.setValue("allDay", e.target.checked);
                }}
              />
              <p>all day</p>
            </label>
          </div>
          <FormField name="notes">
            <label className={style.addLetterFormLabel}>Reason</label>
            <textarea
              className="form-control textareaform-control"
              style={{ minHeight: "100px" }}
              {...form.register("reason")}
            />
          </FormField>
          <div className={style.buttonGroup}>
            <Button
              type="button"
              variant="outline"
              size="large"
              disabled={isAddPending || isEditPending}
              onClick={() => {
                closeModal();
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="large"
              disabled={isAddPending || isEditPending}
              onClick={form.handleSubmit(handleFormSubmit, handleInvalidSubmit)}
            >
              Confirm
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};
